import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import VendorEditModal, { type VendorEditData } from '../vendor-detail/modals/vendorEditModal';
import type { VendorBasicInfo } from '../vendor-detail/types';

type SelfProduct = {
  productIdx: number;
  name: string;
  salesPrice: number;
  costPrice: number;
  status: string;
  description: string;
};

export default function MyVendorPage() {
  const [myInfo, setMyInfo] = useState<VendorBasicInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'services'>('info');

  // 자사 상품(서비스) 관련
  const [selfProducts, setSelfProducts] = useState<SelfProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState<SelfProduct | null>(null);

  const fetchMyInfo = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status) setMyInfo(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch my info:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSelfProducts = useCallback(async () => {
    if (!myInfo?.vendorIdx) return;
    setIsLoadingProducts(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/product?partnerVendorIdx=${myInfo.vendorIdx}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const items = result.items || result.data?.items || [];
      setSelfProducts(items);
    } catch (error) {
      console.error('Failed to fetch self products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  }, [myInfo?.vendorIdx]);

  useEffect(() => { fetchMyInfo(); }, [fetchMyInfo]);
  useEffect(() => {
    if (activeTab === 'services' && myInfo?.vendorIdx) fetchSelfProducts();
  }, [activeTab, myInfo?.vendorIdx, fetchSelfProducts]);

  const handleUpdateMyVendor = async (data: VendorEditData) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/me', {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        alert('업체 정보가 수정되었습니다.');
        setShowEditModal(false);
        fetchMyInfo();
      } else {
        const err = await response.json();
        alert(`수정 실패: ${err.message}`);
      }
    } catch (error) {
      console.error('Update Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const handleSaveService = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = {
      partnerVendorIdx: myInfo?.vendorIdx,
      name: form.get('name'),
      category: '플래너동행서비스',
      costPrice: Number(form.get('costPrice') || 0),
      salesPrice: Number(form.get('salesPrice') || 0),
      stockQuantity: 999,
      status: 'SALE',
      description: form.get('description') || '',
    };

    try {
      const token = localStorage.getItem('accessToken');
      const isEdit = !!editingService;
      const url = isEdit
        ? `https://api.wedmin.biz/product/${editingService.productIdx}`
        : 'https://api.wedmin.biz/product';

      const response = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert(isEdit ? '상품(서비스)이 수정되었습니다.' : '상품(서비스)이 등록되었습니다.');
        setShowServiceModal(false);
        setEditingService(null);
        fetchSelfProducts();
      } else {
        const err = await response.json();
        alert(`실패: ${err.message}`);
      }
    } catch (error) {
      console.error('Save Service Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const handleDeleteService = async (productIdx: number) => {
    if (!window.confirm('정말 이 상품(서비스)을 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/product/${productIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('삭제되었습니다.');
        fetchSelfProducts();
      } else {
        const err = await response.json();
        alert(`삭제 실패: ${err.message}`);
      }
    } catch (error) {
      alert('오류가 발생했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8 overflow-y-auto">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">내 업체 정보</h1>
            <p className="text-sm text-gray-600 mt-1">우리 회사의 기본 정보와 자사 상품(서비스)을 관리합니다.</p>
          </div>
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <i className="ri-edit-line"></i> 정보 수정
          </button>
        </div>

        {/* 탭 */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {[
            { key: 'info' as const, label: '기본 정보', icon: 'ri-information-line' },
            { key: 'services' as const, label: '자사 상품(서비스)', icon: 'ri-service-line' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={tab.icon}></i> {tab.label}
            </button>
          ))}
        </div>

        {/* 기본 정보 탭 */}
        {activeTab === 'info' && myInfo && (
          <div className="space-y-6 max-w-5xl">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">기본 정보</h3>
              <div className="grid grid-cols-2 gap-6">
                <InfoItem label="업체명" value={myInfo.name} />
                <InfoItem label="사업자번호" value={myInfo.businessNumber} />
                <InfoItem label="대표자/담당자" value={myInfo.managerName} />
                <InfoItem label="연락처" value={myInfo.contact} />
                <InfoItem label="이메일" value={myInfo.email} />
                <InfoItem label="상태" value={
                  <span className={`px-2 py-1 rounded text-xs font-medium ${myInfo.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {myInfo.status}
                  </span>
                } />
                <div className="col-span-2">
                  <InfoItem label="주소" value={myInfo.address} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">정산 정보</h3>
              <div className="grid grid-cols-2 gap-6">
                <InfoItem label="은행명" value={myInfo.bankName} />
                <InfoItem label="계좌번호" value={myInfo.accountNumber} />
                <InfoItem label="예금주" value={myInfo.accountHolder} />
                <InfoItem label="수수료율" value={`${myInfo.commissionRate}%`} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">메모</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{myInfo.memo || '등록된 메모가 없습니다.'}</p>
            </div>
          </div>
        )}

        {activeTab === 'info' && !myInfo && (
          <div className="text-center py-20 text-gray-500">정보를 불러올 수 없습니다.</div>
        )}

        {/* 자사 상품(서비스) 탭 */}
        {activeTab === 'services' && (
          <div className="max-w-5xl">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">자사 상품(서비스) 목록</h3>
                  <p className="text-sm text-gray-500 mt-1">고객 계약 시 선택할 수 있는 자사 상품(서비스)을 관리합니다.</p>
                </div>
                <button
                  onClick={() => { setEditingService(null); setShowServiceModal(true); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                >
                  <i className="ri-add-line"></i> 상품(서비스) 등록
                </button>
              </div>

              {isLoadingProducts ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : selfProducts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">상품(서비스)명</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">판매가</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">공급가</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">상태</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">관리</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selfProducts.map((prod) => (
                        <tr key={prod.productIdx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{prod.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{prod.salesPrice.toLocaleString()}원</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{prod.costPrice.toLocaleString()}원</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              prod.status === 'SALE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}>{prod.status}</span>
                          </td>
                          <td className="px-6 py-4 text-right text-sm">
                            <button
                              onClick={() => { setEditingService(prod); setShowServiceModal(true); }}
                              className="text-blue-600 hover:text-blue-900 mr-3 cursor-pointer"
                            >수정</button>
                            <button
                              onClick={() => handleDeleteService(prod.productIdx)}
                              className="text-red-600 hover:text-red-900 cursor-pointer"
                            >삭제</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center text-gray-400">
                  <i className="ri-service-line text-4xl mb-2 block"></i>
                  등록된 자사 상품(서비스)이 없습니다.
                </div>
              )}
            </div>
          </div>
        )}

        {/* 상품(서비스) 등록/수정 모달 */}
        {showServiceModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-800">{editingService ? '상품(서비스) 수정' : '상품(서비스) 등록'}</h2>
                <button onClick={() => { setShowServiceModal(false); setEditingService(null); }} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form onSubmit={handleSaveService} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상품(서비스)명 *</label>
                  <input type="text" name="name" defaultValue={editingService?.name} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="예: 플래너 동행 (본식)" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">판매가 (고객가) *</label>
                    <input type="number" name="salesPrice" defaultValue={editingService?.salesPrice} required min="0"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="예: 300000" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">공급가 (원가)</label>
                    <input type="number" name="costPrice" defaultValue={editingService?.costPrice || 0} min="0"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="0" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                  <textarea name="description" defaultValue={editingService?.description} rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                    placeholder="서비스에 대한 설명을 입력하세요" />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setShowServiceModal(false); setEditingService(null); }}
                    className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">취소</button>
                  <button type="submit"
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    {editingService ? '수정 완료' : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <VendorEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          initialData={myInfo}
          onSave={handleUpdateMyVendor}
        />
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string, value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500 mb-1">{label}</dt>
      <dd className="text-base text-gray-900 font-medium">{value || '-'}</dd>
    </div>
  );
}
